import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useFeatures } from 'flagged';

import { Drawer, List, Divider, ListSubheader, Hidden } from '@material-ui/core';
import Collapse from '@material-ui/core/Collapse';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import {
	DateRange,
	ExpandLess,
	ExpandMore,
	Pages,
	Sports,
	SportsBaseball,
	SportsBasketball,
	SportsCricket,
	SportsFootball,
	SportsGolf,
	SportsMotorsports,
	SportsSoccer,
	Tv
} from '@material-ui/icons';

import messages from './messages';
import { fetchAppConfigByProfileName } from '../Appconfig';
import { AuthLink } from '../Auth';
import { ListItemLink, ListItemATagLink } from '../ListItemLink';
import awsConfig from '../../../aws-exports';
import nowLogo from '../../../imgs/logos/now-logo.png';
import peacockLogo from '../../../imgs/logos/peacock-logo.png';
import skyLogo from '../../../imgs/logos/sky-logo.png';
import wowLogo from '../../../imgs/logos/wow-logo.png';
import { DailyTestGame } from '../../../utils/types';

import './sidebar.css';

const getLogo = (accountName: string): { src: string; brand: string } => {
	const lowerAccountName = accountName.toLowerCase();
	if (lowerAccountName.includes('peacock')) return { src: peacockLogo, brand: 'peacock' };
	if (lowerAccountName.includes('wow')) return { src: wowLogo, brand: 'wow' };
	if (lowerAccountName.includes('now')) return { src: nowLogo, brand: 'now' };
	return { src: skyLogo, brand: 'sky' };
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		drawer: ({ width }: StyleProps) => ({
			[theme.breakpoints.up('lg')]: {
				width,
				flexShrink: 0
			}
		}),
		drawerPaper: ({ width }: StyleProps) => ({
			width
		}),
		drawerHeader: {
			display: 'flex',
			alignItems: 'center',
			padding: theme.spacing(0, 1),
			// necessary for content to be below app bar
			...theme.mixins.toolbar,
			justifyContent: 'center'
		},
		dailyTestingIcon: {
			position: 'relative',
			top: 7
		},
		dailyTestingExpandIcon: {
			position: 'absolute',
			top: 7,
			left: 160
		},
		scheduleExpandIcon: {
			position: 'absolute',
			top: 11,
			left: 160
		},
		unPaddedList: {
			paddingTop: 0,
			paddingBottom: 0
		}
	})
);

const Sidebar: React.FC<SidebarProps> = ({ width, mobileOpen, onCloseMobileSidebar }) => {
	const [footballOpen, setFootballOpen] = useState(false);
	const [golfOpen, setGolfOpen] = useState(false);
	const [f1Open, setF1Open] = useState(false);
	const [cricketOpen, setCricketOpen] = useState(false);
	const [amFootballOpen, setAmFootballOpen] = useState(false);
	const [baseballOpen, setBaseballOpen] = useState(false);
	const [basketballOpen, setBasketballOpen] = useState(false);

	const [scheduleOpen, setScheduleOpen] = useState(false);
	const [apiDocsOpen, setApiDocsOpen] = useState(false);

	const [scheduleLinks, setScheduleLinks] = useState<Record<string, string>>({});
	const [apiLinks, setApiLinks] = useState<Record<string, string>>({});
	const [recapDailyTestGames, setRecapDailyTestGames] = useState<Record<string, DailyTestGame[]>>({});

	const classes = useStyles({ width });
	const intl = useIntl();
	const { src: logoSrc, brand: logoBrand } = getLogo(awsConfig.accountName);
	const {
		cricketRecapSchedule,
		dailyTestingAmericanFootball,
		dailyTestingBaseball,
		dailyTestingBasketball,
		dailyTestingCricket,
		dailyTestingF1,
		dailyTestingFootball,
		dailyTestingGolf,
		f1RecapSchedule,
		germanFootballRecapSchedule,
		italianFootballRecapSchedule,
		peacockApi,
		peacockLemFeedSchedule,
		skyApi,
		ukFootballRecapSchedule,
		wowApi
	} = useFeatures();

	useEffect(() => {
		const getAppConfigs = async (): Promise<void> => {
			const mgmtUiAppconfigConfigsProfile = await fetchAppConfigByProfileName('mgmt_ui_appconfig_configs_profile');
			const dailyTestGameConfigsProfile = await fetchAppConfigByProfileName('daily-test-game-configs');
			if (!mgmtUiAppconfigConfigsProfile || !dailyTestGameConfigsProfile) {
				return;
			}
			const mgmtUiConfigs = JSON.parse(mgmtUiAppconfigConfigsProfile);
			const dailyTestGames = JSON.parse(dailyTestGameConfigsProfile);
			if (mgmtUiConfigs.scheduleLinks) setScheduleLinks(mgmtUiConfigs.scheduleLinks);
			if (mgmtUiConfigs.apiLinks) setApiLinks(mgmtUiConfigs.apiLinks);
			setRecapDailyTestGames(dailyTestGames[awsConfig.accountName] ?? dailyTestGames['development'] ?? {});
		};
		getAppConfigs();
	}, []);

	const getDailyTestingGames = (sport: string, recapDailyGames: DailyTestGame[], icon: JSX.Element, open: boolean, setOpen): JSX.Element => {
		const games = recapDailyGames.map((eachGame) => {
			return {
				title: eachGame.abbrTitle ?? eachGame.gameObject.title,
				link: `/games/${eachGame.gameObject.id}`
			};
		});
		return (
			<List
				className={classes.unPaddedList}
				subheader={
					<ListSubheader
						component='div'
						onClick={(): void => {
							setOpen(!open);
						}}>
						<span className={classes.dailyTestingIcon}>{icon}</span> {sport}
						<span className={classes.dailyTestingExpandIcon}> {open ? <ExpandLess /> : <ExpandMore />}</span>
					</ListSubheader>
				}>
				<Collapse in={open} timeout={200} unmountOnExit>
					{games.map(({ link, title }, idx) => (
						<ListItemLink to={link} label={title} key={`link_${idx}`} />
					))}
				</Collapse>
			</List>
		);
	};

	const drawerChildren: JSX.Element = (
		<>
			<div className={classes.drawerHeader}>
				<img src={logoSrc} alt={awsConfig.accountName} className={`sidebar__logo sidebar__logo--${logoBrand}`} />
			</div>

			<Divider />

			<List className={classes.unPaddedList}>
				<ListItemLink
					to='/channels'
					label={intl.formatMessage({ ...messages.channels })}
					icon={<Tv style={{ marginRight: '0.5rem' }} />}
					testid='sidebar-items-channels'
				/>
				<ListItemLink
					to='/games'
					label={intl.formatMessage({ ...messages.games })}
					icon={<Sports style={{ marginRight: '0.5rem' }} />}
					testid='sidebar-items-games'
				/>
			</List>

			<Divider />

			<ListSubheader component='div'>{intl.formatMessage({ ...messages.dailyTests })}</ListSubheader>
			{dailyTestingFootball && recapDailyTestGames['football'] && recapDailyTestGames['football'].length ? (
				getDailyTestingGames(
					intl.formatMessage({ ...messages.football }),
					recapDailyTestGames['football'],
					<SportsSoccer style={{ marginRight: '0.5rem' }} />,
					footballOpen,
					setFootballOpen
				)
			) : (
				<></>
			)}
			{dailyTestingGolf && recapDailyTestGames['golf'] && recapDailyTestGames['golf'].length ? (
				getDailyTestingGames(
					intl.formatMessage({ ...messages.golf }),
					recapDailyTestGames['golf'],
					<SportsGolf style={{ marginRight: '0.5rem' }} />,
					golfOpen,
					setGolfOpen
				)
			) : (
				<></>
			)}
			{dailyTestingF1 && recapDailyTestGames['f1'] && recapDailyTestGames['f1'].length ? (
				getDailyTestingGames(
					intl.formatMessage({ ...messages.f1 }),
					recapDailyTestGames['f1'],
					<SportsMotorsports style={{ marginRight: '0.5rem' }} />,
					f1Open,
					setF1Open
				)
			) : (
				<></>
			)}
			{dailyTestingCricket && recapDailyTestGames['cricket'] && recapDailyTestGames['cricket'].length ? (
				getDailyTestingGames(
					intl.formatMessage({ ...messages.cricket }),
					recapDailyTestGames['cricket'],
					<SportsCricket style={{ marginRight: '0.5rem' }} />,
					cricketOpen,
					setCricketOpen
				)
			) : (
				<></>
			)}
			{dailyTestingAmericanFootball && recapDailyTestGames['americanfootball'] && recapDailyTestGames['americanfootball'].length ? (
				getDailyTestingGames(
					intl.formatMessage({ ...messages.americanfootball }),
					recapDailyTestGames['americanfootball'],
					<SportsFootball style={{ marginRight: '0.5rem' }} />,
					amFootballOpen,
					setAmFootballOpen
				)
			) : (
				<></>
			)}
			{dailyTestingBaseball && recapDailyTestGames['baseball'] && recapDailyTestGames['baseball'].length ? (
				getDailyTestingGames(
					intl.formatMessage({ ...messages.baseball }),
					recapDailyTestGames['baseball'],
					<SportsBaseball style={{ marginRight: '0.5rem' }} />,
					baseballOpen,
					setBaseballOpen
				)
			) : (
				<></>
			)}
			{dailyTestingBasketball && recapDailyTestGames['basketball'] && recapDailyTestGames['basketball'].length ? (
				getDailyTestingGames(
					intl.formatMessage({ ...messages.basketball }),
					recapDailyTestGames['basketball'],
					<SportsBasketball style={{ marginRight: '0.5rem' }} />,
					basketballOpen,
					setBasketballOpen
				)
			) : (
				<></>
			)}

			<Divider />

			<List
				onClick={(): void => {
					setApiDocsOpen(!apiDocsOpen);
				}}
				subheader={
					<ListSubheader component='div'>
						{intl.formatMessage({ ...messages.apiDocs })}{' '}
						<span className={classes.dailyTestingExpandIcon}> {apiDocsOpen ? <ExpandLess /> : <ExpandMore />}</span>
					</ListSubheader>
				}
				className={classes.unPaddedList}>
				<Collapse in={apiDocsOpen} timeout={200} unmountOnExit>
					{skyApi && (
						<ListItemATagLink
							href={apiLinks.sky}
							target='blank'
							label={intl.formatMessage({ ...messages.skyApi })}
							icon={<Pages style={{ marginRight: '0.5rem' }} />}
						/>
					)}
					{peacockApi && (
						<ListItemATagLink
							href={apiLinks.peacock}
							target='blank'
							label={intl.formatMessage({ ...messages.peacockApi })}
							icon={<Pages style={{ marginRight: '0.5rem' }} />}
						/>
					)}
					{wowApi && (
						<ListItemATagLink
							href={apiLinks.wow}
							target='blank'
							label={intl.formatMessage({ ...messages.wowApi })}
							icon={<Pages style={{ marginRight: '0.5rem' }} />}
						/>
					)}
				</Collapse>
			</List>

			<Divider />

			<List
				onClick={(): void => {
					setScheduleOpen(!scheduleOpen);
				}}
				subheader={
					<ListSubheader component='div'>
						{intl.formatMessage({ ...messages.recapSchedule })}{' '}
						<span className={classes.dailyTestingExpandIcon}> {scheduleOpen ? <ExpandLess /> : <ExpandMore />}</span>
					</ListSubheader>
				}
				className={classes.unPaddedList}>
				<Collapse in={scheduleOpen} timeout={200} unmountOnExit>
					{ukFootballRecapSchedule && (
						<ListItemATagLink
							href={scheduleLinks.ukFootballRecapScheduleLink}
							target='blank'
							label={intl.formatMessage({ ...messages.ukFootballSchedule })}
							icon={<DateRange style={{ marginRight: '0.5rem' }} />}
						/>
					)}
					{germanFootballRecapSchedule && (
						<ListItemATagLink
							href={scheduleLinks.germanFootballRecapScheduleLink}
							target='blank'
							label={intl.formatMessage({ ...messages.germanFootballSchedule })}
							icon={<DateRange style={{ marginRight: '0.5rem' }} />}
						/>
					)}
					{italianFootballRecapSchedule && (
						<ListItemATagLink
							href={scheduleLinks.italianFootballRecapScheduleLink}
							target='blank'
							label={intl.formatMessage({ ...messages.italianFootballSchedule })}
							icon={<DateRange style={{ marginRight: '0.5rem' }} />}
						/>
					)}
					{f1RecapSchedule && (
						<ListItemATagLink
							href={scheduleLinks.f1RecapScheduleLink}
							target='blank'
							label={intl.formatMessage({ ...messages.f1Schedule })}
							icon={<DateRange style={{ marginRight: '0.5rem' }} />}
						/>
					)}
					{cricketRecapSchedule && (
						<ListItemATagLink
							href={scheduleLinks.cricketRecapScheduleLink}
							target='blank'
							label={intl.formatMessage({ ...messages.cricketSchedule })}
							icon={<DateRange style={{ marginRight: '0.5rem' }} />}
						/>
					)}
					{peacockLemFeedSchedule && (
						<ListItemATagLink
							href={scheduleLinks.peacockLemFeedScheduleLink}
							target='blank'
							label={intl.formatMessage({ ...messages.peacockLemFeed })}
							icon={<DateRange style={{ marginRight: '0.5rem' }} />}
						/>
					)}
				</Collapse>
			</List>

			<Divider />

			<AuthLink />
		</>
	);

	return (
		<nav className={classes.drawer}>
			<Hidden smUp implementation='css'>
				<Drawer
					data-testid='sidebar-mobile'
					variant='temporary'
					anchor='left'
					classes={{
						paper: classes.drawerPaper
					}}
					open={mobileOpen}
					onClose={onCloseMobileSidebar}
					ModalProps={{
						keepMounted: true // Better open performance on mobile.
					}}>
					<div onClick={onCloseMobileSidebar}>{drawerChildren}</div>
				</Drawer>
			</Hidden>
			<Hidden mdDown implementation='css'>
				<Drawer
					variant='permanent'
					anchor='left'
					classes={{
						paper: classes.drawerPaper
					}}
					open>
					{drawerChildren}
				</Drawer>
			</Hidden>
		</nav>
	);
};

export default Sidebar;

type StyleProps = {
	width: number;
};

type SidebarProps = {
	width: number;
	mobileOpen: boolean;
	onCloseMobileSidebar: React.MouseEventHandler<HTMLButtonElement | HTMLDivElement>;
};
